import { createStore, createEffect, createEvent, guard } from "effector";
import { chatsAPI } from "./api";
import {
  MIndividualChatsList,
  MGroupChatsList,
  MChatById,
  MIndividualChatsMessage,
  IndividualChat,
  Message,
  GroupChatList,
  MChatChange,
} from "./types";
import { accountAPI } from "entities/account";

/**
 * @info
 * @GET_individual_chat_list
 *
 */

const getIndividualChatsListFx = createEffect({
  handler: async () => {
    const response = await chatsAPI.fetchAllIndividualChatsList();
    return response.data;
  },
});

// Событие для обновления счетчика непрочитанных сообщений в индивидуальном чате
const updateUnreadMessagesInIndividualChat = createEvent<string>(); // chatId

// store individual chat list
const $individualChatsListStore =
  createStore<MIndividualChatsList.Response | null>(null)
    .on(getIndividualChatsListFx.doneData, (_, data) => data)
    .on(updateUnreadMessagesInIndividualChat, (state, chatId) => {
      if (!state) return null;

      const updatedChats = state.chats.map((chat) => {
        if (chat.id === chatId) {
          // Обнуляем счетчик непрочитанных сообщений
          return { ...chat, unread_messages: 0 };
        }
        return chat;
      });

      return { ...state, chats: updatedChats };
    });

/**
 * @info
 * @GET_group_chat_list
 *
 */
//===============================================================================
const getGroupChatsListFx = createEffect({
  handler: async () => {
    const response = await chatsAPI.fetchAllGroupChatsList();
    return response.data;
  },
});

// Событие для обновления счетчика непрочитанных сообщений в групповом чате
const updateUnreadMessagesInGroupChat = createEvent<string>(); // chatId

// store group chat list
const $groupChatsListStore = createStore<MGroupChatsList.Response | null>(null)
  .on(getGroupChatsListFx.doneData, (_, data) => data)
  .on(updateUnreadMessagesInGroupChat, (state, chatId) => {
    if (!state) return null;

    const updatedChats = state.chats.map((chat) => {
      if (chat.id_group_chat === chatId) {
        // Обнуляем счетчик непрочитанных сообщений
        return { ...chat, unread_messages: 0 };
      }
      return chat;
    });

    return { ...state, chats: updatedChats };
  });

const setCurrentGroupChatEv = createEvent<GroupChatList>();
const $currentGroupChat = createStore<GroupChatList | null>(null).on(
  setCurrentGroupChatEv,
  (_, data) => data,
);

const getGroupChatMessageFx = createEffect({
  handler: async (param: MIndividualChatsMessage.Params) => {
    const response = await chatsAPI.getIndividualChatMessage(param);
    return response.data;
  },
});

/**
 * @info
 * @POST_chat_by_id
 *
 */

const postChatByIdFx = createEffect({
  handler: async (param: MChatById.Params) => {
    const response = await chatsAPI.getChatById(param);
    return response.data;
  },
});

// store chat by id
const $chatByIdStore = createStore<IndividualChat | GroupChatList | null>(
  null,
).on(postChatByIdFx.doneData, (_, data) => data);

/**
 * @info
 * @GET_individual_chat_message
 *
 * @param
 * chat_id, limit, offset, type_chat
 */
const getIndividualChatMessageFx = createEffect({
  handler: async (param: MIndividualChatsMessage.Params) => {
    const response = await chatsAPI.getIndividualChatMessage(param);

    // Обрабатываем ответ, чтобы пометить закрепленные сообщения
    const data = response.data;

    // Проверяем, есть ли в ответе массив attached
    if (
      data.attached &&
      Array.isArray(data.attached) &&
      data.attached.length > 0
    ) {
      // Помечаем сообщения из массива attached как закрепленные
      data.messages = data.messages.map((message) => {
        if (data.attached.includes(message.id)) {
          return { ...message, is_pinned: true };
        }
        return message;
      });
    } else if (
      data.attached &&
      typeof data.attached === "string" &&
      data.attached
    ) {
      // Если attached - это строка с одним ID
      data.messages = data.messages.map((message) => {
        if (message.id === data.attached) {
          return { ...message, is_pinned: true };
        }
        return message;
      });
    }

    return data;
  },
});

/**
 * @info
 * @GET_pinned_messages
 *
 * @param
 * chat_id, type_chat
 */
const getPinnedMessagesFx = createEffect({
  handler: async (param: { chat_id: string; type_chat: "solo" | "group" }) => {
    try {
      // Вместо вызова отдельного API для закрепленных сообщений,
      // используем основной API для получения сообщений чата
      const response = await chatsAPI.getIndividualChatMessage({
        chat_id: param.chat_id,
        type_chat: param.type_chat,
      });

      // Получаем данные из ответа
      const data = response.data;

      // Если есть массив attached, используем его для фильтрации сообщений
      if (
        data.attached &&
        Array.isArray(data.attached) &&
        data.attached.length > 0
      ) {
        // Фильтруем сообщения, оставляя только те, которые есть в массиве attached
        const pinnedMessages = data.messages.filter((message) =>
          data.attached.includes(message.id),
        );

        // Помечаем эти сообщения как закрепленные
        pinnedMessages.forEach((message) => {
          message.is_pinned = true;
        });

        // Возвращаем только закрепленные сообщения
        return {
          attached: data.attached,
          messages: pinnedMessages,
        };
      }

      // Если attached нет или он пустой, возвращаем пустой массив
      return { attached: "", messages: [] };
    } catch (error) {
      console.error("Ошибка при загрузке закрепленных сообщений:", error);
      return { attached: "", messages: [] };
    }
  },
});

// Хранилище для закрепленных сообщений
const $pinnedMessagesStore = createStore<MIndividualChatsMessage.Response>({
  attached: "",
  messages: [],
}).on(getPinnedMessagesFx.doneData, (_, data) => data);

// Добавляем событие для добавления нового сообщения
const addMessage = createEvent<Message>();

// Добавляем событие для удаления сообщения
const removeMessage = createEvent<string>(); // ID сообщения для удаления

// Добавляем события для закрепления/открепления сообщения
const pinMessage = createEvent<string>(); // ID сообщения для закрепления
const unpinMessage = createEvent<string>(); // ID сообщения для открепления

const $individualChatMessageStore =
  createStore<MIndividualChatsMessage.Response | null>(null)
    .on(getIndividualChatMessageFx.doneData, (_, data) => data)
    .on(addMessage, (state, newMessage) => {
      if (state) {
        const updatedMessages = [newMessage, ...state.messages]; // добавляем новое сообщение в начало массива
        return { ...state, messages: updatedMessages };
      } else {
        return null;
      }
    })
    .on(removeMessage, (state, messageId) => {
      if (state && state.messages) {
        // Фильтруем сообщения, исключая сообщение с указанным ID
        const updatedMessages = state.messages.filter(
          (msg) => msg.id !== messageId,
        );
        return { ...state, messages: updatedMessages };
      }
      return state;
    })
    .on(pinMessage, (state, messageId) => {
      if (state && state.messages) {
        // Обновляем сообщение, устанавливая флаг is_pinned
        const updatedMessages = state.messages.map((msg) =>
          msg.id === messageId ? { ...msg, is_pinned: true } : msg,
        );
        return { ...state, messages: updatedMessages };
      }
      return state;
    })
    .on(unpinMessage, (state, messageId) => {
      if (state && state.messages) {
        // Обновляем сообщение, снимая флаг is_pinned
        const updatedMessages = state.messages.map((msg) =>
          msg.id === messageId ? { ...msg, is_pinned: false } : msg,
        );
        return { ...state, messages: updatedMessages };
      }
      return state;
    });

const setCurrentSoloChatEv = createEvent<
  IndividualChat | GroupChatList | null
>();
const $currentSoloChat = createStore<IndividualChat | GroupChatList | null>(
  null,
).on(setCurrentSoloChatEv, (_, data) => data);

// store group chat message
const $groupChatMessageStore =
  createStore<MIndividualChatsMessage.Response | null>(null)
    .on(getGroupChatMessageFx.doneData, (_, data) => data)
    .on(addMessage, (state, newMessage) =>
      state ? { ...state, messages: [...state.messages, newMessage] } : null,
    )
    .on(removeMessage, (state, messageId) => {
      if (state && state.messages) {
        // Фильтруем сообщения, исключая сообщение с указанным ID
        const updatedMessages = state.messages.filter(
          (msg) => msg.id !== messageId,
        );
        return { ...state, messages: updatedMessages };
      }
      return state;
    });

/**
 * @info
 * @PATCH_change_chat
 *
 */

const patchChangeChatAvatarFx = createEffect({
  handler: async (data: MChatChange.ParamsForAvatar) => {
    const response = await chatsAPI.patchChangeChatAvatar(data);
    return response.data;
  },
});

/**
 * @info
 * @PATCH_change_chat
 *
 */

const patchChangeChatNameFx = createEffect({
  handler: async (data: MChatChange.ParamsForName) => {
    const response = await chatsAPI.patchChangeChatName(data);
    return response.data;
  },
});

// tracking upload
const createGuard = (fx: any) => {
  guard({
    source: fx.doneData,
    filter: (data) => data !== null,
  }).watch(() => {
    getGroupChatsListFx();
  });
};

export {
  /**
   * @effects
   */
  getIndividualChatsListFx,
  getGroupChatsListFx,
  postChatByIdFx,
  getIndividualChatMessageFx,
  getGroupChatMessageFx,
  patchChangeChatAvatarFx,
  patchChangeChatNameFx,
  getPinnedMessagesFx,
  /**
   * @events
   */
  setCurrentSoloChatEv,
  addMessage,
  setCurrentGroupChatEv,
  removeMessage,
  updateUnreadMessagesInIndividualChat,
  updateUnreadMessagesInGroupChat,
  pinMessage,
  unpinMessage,
  /**
   * @store
   */
  $individualChatsListStore,
  $groupChatsListStore,
  $chatByIdStore,
  $individualChatMessageStore,
  $currentSoloChat,
  $currentGroupChat,
  $groupChatMessageStore,
  $pinnedMessagesStore,
};
